import { ControllerFlowAPI, ControllerParams } from '@wix/yoshi-flow-editor';
import { CalendarState } from '../components/BookingCalendar/controller';
import { CalendarContext } from '../utils/context/contextFactory';
import {
  ListEventTimeSlotsRequest,
  ListAvailabilityTimeSlotsRequest,
  ListMultiServiceAvailabilityTimeSlotsRequest,
  CustomerChoices,
  Service as TimeSlotService,
} from '@wix/ambassador-bookings-availability-v2-time-slot/types';
import {
  Service,
  LocationType as ServiceLocationType,
  ServiceType,
} from '@wix/ambassador-bookings-services-v2-service/types';
import { DO_NOT_CARE_STAFF, STAFF_RESOURCE_ID } from '../constants/constants';
import { FilterOptions, SlotsAvailability } from '../types/types';
import { BookingAdapter } from '@wix/bookings-checkout-api';
import { isLayoutWithTimeSlot } from '../utils/layouts';
import { isOnlyBusinessLocationsSelected } from '../utils/selectedLocations/selectedLocations';
import { CalendarServiceSelections } from '@wix/bookings-catalog-calendar-viewer-utils';
import { listEventTimeSlots } from './BookingAPIS/listEventTimeSlots';
import { listAvailabilityTimeSlots } from './BookingAPIS/listAvailabilityTimeSlots';
import { listMultiServiceAvailabilityTimeSlots } from './BookingAPIS/listMultiServiceAvailabilityTimeSlots';
import {
  isCustomerCannotChooseStaff,
  isStaffVariant,
} from '@wix/bookings-calendar-catalog-viewer-mapper';

export enum Flow {
  CLASS = 'class',
  SINGLE_APPOINTMENT = 'single-appointment',
  MULTI_APPOINTMENTS = 'multi-appointments',
}

export const MAX_SERVICES_TO_QUERY = 100;

export type QueryAvailabilityRequestUnion =
  | {
      flow: Flow.CLASS;
      requestObject: ListEventTimeSlotsRequest;
    }
  | {
      flow: Flow.SINGLE_APPOINTMENT;
      requestObject: ListAvailabilityTimeSlotsRequest;
    }
  | {
      flow: Flow.MULTI_APPOINTMENTS;
      requestObject: ListMultiServiceAvailabilityTimeSlotsRequest;
    };

export const fetchAvailability = async ({
  from,
  to,
  getNextAvailableSlot,
  shouldLimitPerDay,
  state,
  context,
  flowAPI,
  reportError,
  settingsParams,
}: {
  from: string;
  to: string;
  getNextAvailableSlot?: boolean;
  shouldLimitPerDay?: boolean;
  state: CalendarState;
  context: CalendarContext;
  flowAPI: ControllerFlowAPI;
  reportError: ControllerParams['flowAPI']['reportError'];
  settingsParams: any;
}) => {
  const availabilityCalendarRequest = buildQueryAvailabilityRequest({
    from,
    to,
    state,
    context,
    flowAPI,
    reportError,
    settingsParams,
    getNextAvailableSlot,
    shouldLimitPerDay,
  });

  let slotAvailability;
  if (availabilityCalendarRequest.flow === Flow.CLASS) {
    slotAvailability = await listEventTimeSlots(
      availabilityCalendarRequest.requestObject,
      flowAPI,
      state,
    );
  } else if (availabilityCalendarRequest.flow === Flow.SINGLE_APPOINTMENT) {
    slotAvailability = await listAvailabilityTimeSlots(
      availabilityCalendarRequest.requestObject,
      flowAPI,
      state,
    );
  } else if (availabilityCalendarRequest.flow === Flow.MULTI_APPOINTMENTS) {
    slotAvailability = await listMultiServiceAvailabilityTimeSlots(
      availabilityCalendarRequest.requestObject,
      flowAPI,
      state,
    );
  }

  return slotAvailability;
};

const buildQueryAvailabilityRequest = ({
  from,
  to,
  state,
  shouldLimitPerDay = false,
  getNextAvailableSlot = false,
  context,
  flowAPI,
  reportError,
  settingsParams,
}: {
  from: string;
  to: string;
  state: CalendarState;
  context: CalendarContext;
  flowAPI: ControllerFlowAPI;
  reportError: ControllerParams['flowAPI']['reportError'];
  shouldLimitPerDay?: boolean;
  getNextAvailableSlot?: boolean;
  settingsParams: any;
}): QueryAvailabilityRequestUnion => {
  const {
    selectedTimezone,
    filterOptions,
    servicesInView,
    serviceVariantsMap,
  } = state;
  const { calendarSelections, bookingsSettings } = context;

  if (!selectedTimezone) {
    reportError(`TimeZone does not exist - ${selectedTimezone}`);
  }

  const flow = getFlowFromCalendarState({ state, context });

  if (flow === Flow.CLASS) {
    const serviceIds = buildServiceFilter({
      servicesInView,
      filterOptions,
    });
    const onlyAvailableSlots =
      flowAPI.settings.get(settingsParams.slotsAvailability) ===
      SlotsAvailability.ONLY_AVAILABLE;
    const isAnyResource =
      filterOptions.STAFF_MEMBER.includes(DO_NOT_CARE_STAFF);

    const businessLocationsFilter =
      getBusinessLocationsFilterForQueryAvailabilityRequest(
        state,
        flowAPI.settings,
        settingsParams,
      );

    const openSpotsFilter = getOpenSpotsFilterForQueryAvailabilityRequest({
      state,
      getNextSlotNotFullAndNotTooLateToBook:
        getNextAvailableSlot && !onlyAvailableSlots,
    });

    return {
      flow: Flow.CLASS,
      requestObject: {
        timeZone: selectedTimezone!,
        fromLocalDate: from,
        toLocalDate: to,
        serviceIds,
        ...(shouldLimitPerDay ? { maxSlotsPerDay: 1 } : {}),
        ...(onlyAvailableSlots
          ? { minBookableCapacity: 1, includeNonBookable: false }
          : { includeNonBookable: true }),
        eventFilter: {
          ...(!isAnyResource && filterOptions.STAFF_MEMBER.length > 0
            ? { 'resources.id': { $hasSome: filterOptions.STAFF_MEMBER } }
            : {}),
          ...(businessLocationsFilter['location.businessLocation.id']
            ? {
                'location.id':
                  businessLocationsFilter['location.businessLocation.id'],
              }
            : {}),
          ...openSpotsFilter,
        },
        ...(getNextAvailableSlot && !onlyAvailableSlots
          ? { bookingPolicyViolations: { tooLateToBook: false } }
          : {}),
        ...(getNextAvailableSlot
          ? { cursorPaging: { limit: 1 } }
          : { cursorPaging: { limit: 1000 } }),
      },
    };
  } else if (flow === Flow.SINGLE_APPOINTMENT) {
    const onlyAvailableSlots =
      flowAPI.settings.get(settingsParams.slotsAvailability) ===
      SlotsAvailability.ONLY_AVAILABLE;

    const getNextSlotNotFullAndNotTooLateToBook =
      getNextAvailableSlot && !onlyAvailableSlots;

    const businessLocationsFilter =
      getBusinessLocationsFilterForQueryAvailabilityRequest(
        state,
        flowAPI.settings,
        settingsParams,
      );
    const businessLocationId =
      businessLocationsFilter['location.businessLocation.id'];

    const serviceIds = buildServiceFilter({
      servicesInView,
      filterOptions,
    });

    const customerChoices = getCustomerChoices({
      calendarServiceSelections: calendarSelections?.services[0],
    });
    const isCalendarSelectionsEqualToDoNotCareStaff =
      calendarSelections?.services[0]?.resources[0] === DO_NOT_CARE_STAFF;

    const isFilterStaffMemberIncludesDoNotCareStaff =
      filterOptions.STAFF_MEMBER.includes(DO_NOT_CARE_STAFF);

    const isStaffSelectionHidden =
      isCustomerCannotChooseStaff(bookingsSettings) &&
      !isStaffVariant(serviceVariantsMap[serviceIds[0]]);

    const isAnyResource =
      isCalendarSelectionsEqualToDoNotCareStaff ||
      isFilterStaffMemberIncludesDoNotCareStaff ||
      isStaffSelectionHidden;

    const serviceLocationType =
      servicesInView[0]?.locations?.[0]?.type ?? ServiceLocationType.BUSINESS;

    return {
      flow: Flow.SINGLE_APPOINTMENT,
      requestObject: {
        serviceId: serviceIds[0],
        fromLocalDate: from,
        toLocalDate: to,
        timeZone: selectedTimezone!,
        ...(businessLocationId
          ? {
              locations: Array.isArray(businessLocationId)
                ? businessLocationId.map((id) => ({
                    locationType: serviceLocationType,
                    id,
                  }))
                : [
                    {
                      locationType: serviceLocationType,
                      id: businessLocationId,
                    },
                  ],
            }
          : {}),
        ...(!isAnyResource &&
        filterOptions.STAFF_MEMBER.length > 0 &&
        serviceIds[0] !== 'EVENT'
          ? {
              resourceIds: filterOptions.STAFF_MEMBER,
              includeResourceTypeIds: [STAFF_RESOURCE_ID],
            }
          : {}),
        ...(onlyAvailableSlots
          ? { bookable: true, includeNonBookable: false }
          : { includeNonBookable: true }),
        ...(getNextSlotNotFullAndNotTooLateToBook
          ? { bookingPolicyViolations: { tooLateToBook: false } }
          : {}),
        ...(customerChoices ? { customerChoices } : {}),
        ...(shouldLimitPerDay ? { timeSlotsPerDay: 1 } : {}),
        ...(getNextAvailableSlot ? { cursorPaging: { limit: 1 } } : {}),
        shouldReturnAllResources: !isAnyResource,
      },
    };
  } else if (flow === Flow.MULTI_APPOINTMENTS) {
    const onlyAvailableSlots =
      flowAPI.settings.get(settingsParams.slotsAvailability) ===
      SlotsAvailability.ONLY_AVAILABLE;
    const getNextSlotNotFullAndNotTooLateToBook =
      getNextAvailableSlot && !onlyAvailableSlots;
    const businessLocationsFilter =
      getBusinessLocationsFilterForQueryAvailabilityRequest(
        state,
        flowAPI.settings,
        settingsParams,
      );
    const businessLocationId =
      businessLocationsFilter['location.businessLocation.id'];
    const locationType = servicesInView[0].locations?.[0].type!;
    const servicesForRequest = getRulesBasedPreferencesForMultiSlots(
      calendarSelections!.services!,
    );
    return {
      flow: Flow.MULTI_APPOINTMENTS,
      requestObject: {
        services: servicesForRequest,
        fromLocalDate: from,
        toLocalDate: to,
        timeZone: selectedTimezone ?? null,

        location: {
          locationType,
          id: Array.isArray(businessLocationId)
            ? businessLocationId[0]
            : businessLocationId,
        },
        ...(onlyAvailableSlots
          ? { bookable: true, includeNonBookable: false }
          : { includeNonBookable: true }),
        ...(getNextSlotNotFullAndNotTooLateToBook
          ? {
              bookingPolicyViolations: {
                tooLateToBook: false,
              },
            }
          : {}),
        ...(shouldLimitPerDay ? { timeSlotsPerDay: 1 } : {}),
        ...(getNextAvailableSlot ? { cursorPaging: { limit: 1 } } : {}),
      },
    };
  }

  reportError(
    `Unexpected flow state in buildQueryAvailabilityRequest: ${flow}`,
  );
  throw new Error(
    `Unexpected flow state in buildQueryAvailabilityRequest: ${flow}`,
  );
};

const getFlowFromCalendarState = ({
  state,
  context,
}: {
  state: CalendarState;
  context: CalendarContext;
}) => {
  const servicesTypes = new Set(
    state.servicesInView.map((service) => service.type),
  );

  if (servicesTypes.has(ServiceType.CLASS)) {
    return Flow.CLASS;
  } else if (servicesTypes.has(ServiceType.APPOINTMENT)) {
    if (
      context.calendarSelections?.services?.length &&
      context.calendarSelections?.services.length > 1
    ) {
      return Flow.MULTI_APPOINTMENTS;
    }
    return Flow.SINGLE_APPOINTMENT;
  }
  return null;
};

const buildServiceFilter = ({
  servicesInView,
  filterOptions,
}: {
  filterOptions: FilterOptions;
  servicesInView: Service[];
}) => {
  const serviceIds =
    filterOptions.SERVICE.length > 0
      ? filterOptions.SERVICE
      : servicesInView.map((service) => `${service?.id}`);

  return getMaximumSupportedServicesForQueryAvailabilityRequest(serviceIds);
};

const getMaximumSupportedServicesForQueryAvailabilityRequest = (
  availableServiceIds: string[],
) => {
  return availableServiceIds.slice(0, MAX_SERVICES_TO_QUERY);
};

const getBusinessLocationsFilterForQueryAvailabilityRequest = (
  state: CalendarState,
  settings: any,
  settingsParams: any,
) => {
  const { filterOptions } = state;
  const isLayoutWithTimeSlots = isLayoutWithTimeSlot(settings, settingsParams);
  const selectedLocations = settings.get(settingsParams.selectedLocations);
  const shouldFilterByBusinessLocations = isLayoutWithTimeSlots
    ? filterOptions.LOCATION.length > 0
    : isOnlyBusinessLocationsSelected(selectedLocations);

  if (shouldFilterByBusinessLocations) {
    return {
      'location.businessLocation.id': isLayoutWithTimeSlots
        ? filterOptions.LOCATION
        : selectedLocations,
    };
  }
  return {};
};

const getOpenSpotsFilterForQueryAvailabilityRequest = ({
  state,
  getNextSlotNotFullAndNotTooLateToBook,
}: {
  state: CalendarState;
  getNextSlotNotFullAndNotTooLateToBook: boolean;
}) => {
  const { rescheduleBookingDetails, servicesInView } = state;

  const isIndividualService = servicesInView.some(
    (service) => service.type === ServiceType.APPOINTMENT,
  );

  if (rescheduleBookingDetails) {
    const bookingAdapter = new BookingAdapter(rescheduleBookingDetails);
    const numberOfParticipants = bookingAdapter.numberOfParticipants!;
    return { remainingCapacity: { $gte: numberOfParticipants } };
  } else if (isIndividualService || getNextSlotNotFullAndNotTooLateToBook) {
    return { remainingCapacity: { $gte: 1 } };
  }
  return undefined;
};

const getCustomerChoices = ({
  calendarServiceSelections,
}: {
  calendarServiceSelections?: CalendarServiceSelections;
}): CustomerChoices | undefined => {
  const variantChoice = calendarServiceSelections?.variants?.[0]?.choices?.[0];

  const durationInMinutes = variantChoice?.duration?.minutes;

  const durationChoiceId = durationInMinutes ? variantChoice.id : undefined;

  const addOnIds = calendarServiceSelections?.addOns?.map(
    (addon) => addon.addOnId,
  );

  if (!durationChoiceId && !addOnIds?.length) {
    return;
  }

  return {
    durationChoiceId,
    addOnIds,
  };
};

const getRulesBasedPreferencesForMultiSlots = (
  servicesSelections: CalendarServiceSelections[],
): TimeSlotService[] => {
  return servicesSelections.map((selectedService) => {
    const { id: serviceId, resources: resourceIds } = selectedService;

    const customerChoices = getCustomerChoices({
      calendarServiceSelections: selectedService,
    });
    const isAnyStaff = resourceIds.includes(DO_NOT_CARE_STAFF);
    const serviceIdAndCustomerChoices: TimeSlotService = {
      serviceId,
      ...(customerChoices ? { customerChoices } : {}),
    };
    return {
      ...serviceIdAndCustomerChoices,
      ...(isAnyStaff
        ? {}
        : { resourceIds, includeResourceTypeIds: [STAFF_RESOURCE_ID] }),
    };
  });
};
